import { ANY_FAMILY, FAMILIES, familyLabel, type FamilyChoice } from '@couch/shared';
import { useState } from 'react';
import { formatBytes } from './format';
import type { ModelRow as Row } from './model-groups';

export interface RowActions {
  onDelete: (row: Row) => void;
  onFamily: (row: Row, family: FamilyChoice | null) => void;
  /** Only when storage is connected (doc 75). */
  onToLibrary?: ((row: Row) => void) | undefined;
  busy: boolean;
}

/** Where the family came from, and the other families a shared part also serves. */
function note(row: Row, groupId: FamilyChoice | null): string {
  const { file } = row;
  const parts: string[] = [];
  if (file.familySource === 'name') parts.push('guessed from the name');
  if (file.familySource === 'manual') parts.push('your choice');
  const others = file.families.filter((f) => f !== groupId && f !== ANY_FAMILY);
  if (others.length > 0) parts.push(`${groupId === null ? 'used by' : 'also used by'} ${others.map(familyLabel).join(', ')}`);
  return parts.join(' · ');
}

function FamilySelect({ row, unsure, onFamily, busy }: { row: Row; unsure: boolean } & Pick<RowActions, 'onFamily' | 'busy'>) {
  const { file } = row;
  const value = file.familySource === 'manual' ? file.families[0] ?? '' : '';
  return (
    <select aria-label={`Family of ${file.name}`} className="family-select" value={value} disabled={busy}
      onChange={(e) => { onFamily(row, e.target.value === '' ? null : e.target.value as FamilyChoice); }}>
      <option value="">{unsure ? 'Choose a family…' : 'Automatic'}</option>
      {FAMILIES.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
      <option value={ANY_FAMILY}>Works with any model</option>
    </select>
  );
}

/** One installed file: name, size, how Couch knows its family, change family, delete (doc 48). */
export function ModelRow({ row, groupId, actions }: { row: Row; groupId: FamilyChoice | null; actions: RowActions }) {
  const [picking, setPicking] = useState(false);
  const { file } = row;
  const unsure = file.families.length === 0;
  const text = note(row, groupId);
  return (
    <li className="model-row">
      <span className="model-name">{file.name}</span> <span className="muted">{formatBytes(file.sizeBytes)}</span>
      {text && <span className="muted model-note">{text}</span>}
      <span className="model-actions">
        {unsure || picking
          ? <FamilySelect row={row} unsure={unsure} onFamily={(r, f) => { setPicking(false); actions.onFamily(r, f); }} busy={actions.busy} />
          : <button type="button" className="button-quiet" aria-label={`Change family of ${file.name}`} onClick={() => { setPicking(true); }}>Family…</button>}
        {actions.onToLibrary && (
          <button type="button" className="button-quiet" disabled={actions.busy} aria-label={`Send ${file.name} to your library`}
            onClick={() => { actions.onToLibrary?.(row); }}>To library</button>
        )}
        <button type="button" className="button-quiet" disabled={actions.busy} aria-label={`Delete ${file.name} (${formatBytes(file.sizeBytes)})`}
          onClick={() => { actions.onDelete(row); }}>Delete</button>
      </span>
    </li>
  );
}
